import { useState, useCallback } from "react";
import { getErrorMessage } from "../lib/errors/getErrorMessage";

export type DeleteCallback = () => Promise<void>;

export type DeleteDialogController = {
  isOpen: boolean;
  title: string;
  error: string | null;
  isLoading: boolean;

  openDelete: (titleText: string, callback: DeleteCallback) => void;
  closeDelete: () => void;
  confirmDelete: () => Promise<void>;
};



export const useDeleteDialog = (): DeleteDialogController => {
  const [isOpen, setIsOpen] = useState(false);
  const [onDelete, setOnDelete] = useState<DeleteCallback | null>(null);
  const [title, setTitle] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const openDelete = useCallback((titleText: string, callback: DeleteCallback) => {
    setOnDelete(() => callback);
    setTitle(titleText);
    setError(null);
    setIsOpen(true);
  }, []);

  const closeDelete = useCallback(() => {
    if (isLoading) return;

    setIsOpen(false);
    setOnDelete(null);
    setError(null);
  }, [isLoading]);

  const confirmDelete = useCallback(async () => {
    if (!onDelete) return;

    try {
      setIsLoading(true);
      setError(null);

      await onDelete();

      setIsOpen(false);
      setOnDelete(null);
      setError(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [onDelete]);

  return {
    isOpen,
    title,
    error,
    isLoading,
    openDelete,
    closeDelete,
    confirmDelete,
  };
};